import type {
  StormcloudVideoPlayerConfig,
  AdBreak,
  StormcloudApiResponse,
  AdController,
} from "../types";
import type { PalNonceManager } from "../sdk/pal";
import { applyVastMacros, generateCorrelator, normalizeVastTagUrl } from "../utils/vastMacros";
import type { VastConsentSignals } from "../utils/vastMacros";
import { resolveVastEnvironmentSignals } from "../utils/vastEnvironmentSignals";
import {
  buildAnalyticsContext,
  sendHeartbeat,
  sendInitialTracking,
} from "../utils/tracking";

export class AdConfigManager {
  vmapBreaks: AdBreak[] = [];
  consumedVmapBreakIds: Set<string> = new Set();
  apiVastTagUrl: string | undefined;
  apiNumberAds: number | undefined;
  readonly streamCorrelator: string = generateCorrelator();
  readonly viewCorrelator: string = generateCorrelator();
  consentSignals: VastConsentSignals = {};
  podCounter: number = 0;
  podAssignedByPrefetch: boolean = false;

  private lastHeartbeatTime: number = 0;
  private heartbeatInterval: number | undefined;

  private config: StormcloudVideoPlayerConfig;
  private video: HTMLVideoElement;

  constructor(config: StormcloudVideoPlayerConfig, video: HTMLVideoElement) {
    this.config = config;
    this.video = video;
  }

  get debug(): boolean {
    return !!this.config.debugAdTiming;
  }

  isVmapEnabled(): boolean {
    return !!(this.config.isVmap && this.config.vmapUrl?.trim());
  }

  async fetchAdConfiguration(): Promise<void> {
    if (this.isVmapEnabled()) {
      await this.fetchAndParseVmap(this.config.vmapUrl!);
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] VMAP mode enabled");
      }
      return;
    }

    const vastMode = this.config.vastMode || "default";

    if (this.debug) {
      console.log("[StormcloudVideoPlayer] VAST mode:", vastMode);
    }

    if (vastMode === "adstorm") {
      if (!this.config.licenseKey) {
        if (this.debug) {
          console.warn(
            "[StormcloudVideoPlayer] AdStorm mode requires a license key"
          );
        }
        return;
      }

      const vastEndpoint = `https://adstorm.co/api-adstorm-dev/adstorm/vast/${this.config.licenseKey}`;
      this.apiVastTagUrl = vastEndpoint;

      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Using AdStorm VAST endpoint (adstorm mode):",
          vastEndpoint
        );
      }
      return;
    }

    if (this.config.vastTagUrl) {
      this.apiVastTagUrl = normalizeVastTagUrl(this.config.vastTagUrl);
      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Using custom VAST tag URL:",
          this.apiVastTagUrl
        );
      }
      return;
    }

    const apiUrl = "https://adstorm.co/api-adstorm-dev/adstorm/ads/web";

    if (this.debug) {
      console.log(
        "[StormcloudVideoPlayer] Fetching ad configuration from:",
        apiUrl
      );
    }

    const headers: Record<string, string> = {};
    if (this.config.licenseKey) {
      headers["Authorization"] = `Bearer ${this.config.licenseKey}`;
    }

    const response = await fetch(apiUrl, { headers });
    if (!response.ok) {
      if (this.debug) {
        console.warn(
          `[StormcloudVideoPlayer] Failed to fetch ad configuration: ${response.status}`
        );
      }
      return;
    }

    const data: StormcloudApiResponse = await response.json();

    const imaPayload = data.response?.ima?.["publisherdesk.ima"]?.payload;
    if (imaPayload) {
      let decodedPayload = imaPayload;
      try {
        decodedPayload = decodeURIComponent(imaPayload);
      } catch {
        console.warn("[StormcloudVideoPlayer] imaPayload was not URI-encoded; using as-is:", imaPayload);
      }
      console.log("[StormcloudVideoPlayer] Raw VAST tag URL (from API):", decodedPayload);
      this.apiVastTagUrl = normalizeVastTagUrl(decodedPayload);
      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Normalized VAST tag URL:",
          this.apiVastTagUrl
        );
      }
    } else {
      if (this.debug) {
        console.warn(
          "[StormcloudVideoPlayer] No VAST tag URL found in /ads/web response"
        );
      }
    }

    const numberAds = data.response?.options?.vast?.cue_tones?.number_ads;
    if (numberAds != null && numberAds > 0) {
      this.apiNumberAds = numberAds;
      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Number of ads per break from API:",
          this.apiNumberAds
        );
      }
    }
  }

  private async fetchAndParseVmap(vmapUrl: string): Promise<void> {
    if (!vmapUrl.trim()) {
      return;
    }

    try {
      const response = await fetch(vmapUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch VMAP (${response.status})`);
      }

      const vmapXml = await response.text();
      this.vmapBreaks = this.parseVmapToBreaks(vmapXml);
      this.consumedVmapBreakIds.clear();

      if (this.debug) {
        console.log(
          `[StormcloudVideoPlayer] Loaded ${this.vmapBreaks.length} VMAP ad break(s) from:`,
          vmapUrl
        );
      }
    } catch (error) {
      this.vmapBreaks = [];
      this.consumedVmapBreakIds.clear();
      if (this.debug) {
        console.warn("[StormcloudVideoPlayer] Failed to load VMAP:", error);
      }
    }
  }

  private parseVmapToBreaks(vmapXml: string): AdBreak[] {
    if (typeof DOMParser === "undefined") {
      return [];
    }

    const doc = new DOMParser().parseFromString(vmapXml, "application/xml");
    if (doc.querySelector("parsererror")) {
      if (this.debug) {
        console.warn("[StormcloudVideoPlayer] Invalid VMAP XML received");
      }
      return [];
    }

    const VMAP_NS = "http://www.iab.net/videosuite/vmap";
    let adBreakNodes = Array.from(doc.getElementsByTagNameNS(VMAP_NS, "AdBreak"));
    if (adBreakNodes.length === 0) {
      adBreakNodes = Array.from(
        doc.querySelectorAll("AdBreak, vmap\\:AdBreak")
      );
    }
    if (adBreakNodes.length === 0) {
      adBreakNodes = Array.from(doc.getElementsByTagName("*")).filter(
        (el) => el.localName === "AdBreak"
      );
    }
    const parsed: AdBreak[] = [];

    adBreakNodes.forEach((node, index) => {
      const timeOffsetRaw = (node.getAttribute("timeOffset") || "").trim();
      const startTimeMs = this.parseVmapTimeOffsetToMs(timeOffsetRaw);
      if (startTimeMs == null) {
        return;
      }

      let adTagNode = node.getElementsByTagNameNS(VMAP_NS, "AdTagURI")[0];
      if (!adTagNode) {
        adTagNode =
          (node.querySelector("AdTagURI, vmap\\:AdTagURI") as Element | null) ??
          undefined;
      }
      const adTagUrl = (adTagNode?.textContent || "").trim();
      if (!adTagUrl) {
        return;
      }

      const breakId =
        node.getAttribute("breakId") ||
        `vmap-break-${index}-${timeOffsetRaw || "unknown"}`;
      parsed.push({
        id: breakId,
        startTimeMs,
        vastTagUrl: adTagUrl,
      });
    });

    parsed.sort((a, b) => {
      const aStart = a.startTimeMs < 0 ? Number.MAX_SAFE_INTEGER : a.startTimeMs;
      const bStart = b.startTimeMs < 0 ? Number.MAX_SAFE_INTEGER : b.startTimeMs;
      return aStart - bStart;
    });

    return parsed;
  }

  private parseVmapTimeOffsetToMs(timeOffset: string): number | undefined {
    if (!timeOffset) {
      return undefined;
    }

    const normalized = timeOffset.trim().toLowerCase();
    if (normalized === "start") {
      return 0;
    }

    if (normalized === "end") {
      return -1;
    }

    const hms = timeOffset.match(
      /^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/
    );
    if (hms) {
      const [, hh, mm, ss, ms = "0"] = hms;
      const hours = Number(hh);
      const minutes = Number(mm);
      const seconds = Number(ss);
      const millis = Number(ms.padEnd(3, "0").slice(0, 3));
      return ((hours * 3600 + minutes * 60 + seconds) * 1000) + millis;
    }

    const percent = timeOffset.match(/^(\d+(?:\.\d+)?)%$/);
    if (percent) {
      const ratio = Number(percent[1]) / 100;
      const durationSec = this.video.duration;
      if (Number.isFinite(durationSec) && durationSec > 0) {
        return Math.floor(durationSec * 1000 * ratio);
      }
      return undefined;
    }

    return undefined;
  }

  getAdBreakKey(adBreak: AdBreak): string {
    if (adBreak.id) {
      return adBreak.id;
    }
    return `${adBreak.startTimeMs}-${adBreak.vastTagUrl || ""}`;
  }

  resolveBreakStartMs(adBreak: AdBreak): number | undefined {
    if (adBreak.startTimeMs >= 0) {
      return adBreak.startTimeMs;
    }

    if (adBreak.startTimeMs === -1) {
      const durationSec = this.video.duration;
      if (Number.isFinite(durationSec) && durationSec > 0) {
        return Math.floor(durationSec * 1000);
      }
    }

    return undefined;
  }

  findCurrentOrNextBreak(nowMs: number): AdBreak | undefined {
    const schedule: AdBreak[] = this.vmapBreaks;
    let candidate: AdBreak | undefined;
    const tol = this.config.driftToleranceMs ?? 1000;
    for (const b of schedule) {
      if (this.consumedVmapBreakIds.has(this.getAdBreakKey(b))) {
        continue;
      }
      const breakStartMs = this.resolveBreakStartMs(b);
      if (breakStartMs == null) {
        continue;
      }
      if (
        breakStartMs <= nowMs + tol &&
        (candidate == null ||
          breakStartMs > (this.resolveBreakStartMs(candidate) ?? 0))
      ) {
        candidate = b;
      }
    }
    return candidate;
  }

  findBreakForTime(nowMs: number): AdBreak | undefined {
    const schedule: AdBreak[] = this.vmapBreaks;
    for (const b of schedule) {
      if (this.consumedVmapBreakIds.has(this.getAdBreakKey(b))) {
        continue;
      }
      const breakStartMs = this.resolveBreakStartMs(b);
      if (breakStartMs == null) {
        continue;
      }

      if (b.durationMs) {
        const end = breakStartMs + b.durationMs;
        if (nowMs >= breakStartMs && nowMs < end) {
          return b;
        }
        continue;
      }

      if (nowMs >= breakStartMs) {
        return b;
      }
    }
    return undefined;
  }

  selectVastTagsForBreak(b?: AdBreak): string[] | undefined {
    if (!b || !b.vastTagUrl) return undefined;
    if (b.vastTagUrl.includes(",")) {
      return b.vastTagUrl
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    return [b.vastTagUrl];
  }

  beginNewAdPod(): void {
    this.podCounter++;
  }

  private readonly defaultPodMaxAds: number = 6;
  private readonly defaultPodMaxDurationMs: number = 120000;

  resolvePodParams(breakDurationMs?: number): {
    maxAds: number;
    minDurationMs: number;
    maxDurationMs: number;
  } {
    const maxDurationMs =
      breakDurationMs != null && breakDurationMs > 0
        ? breakDurationMs
        : this.config.podMaxDurationMs != null && this.config.podMaxDurationMs > 0
          ? this.config.podMaxDurationMs
          : this.defaultPodMaxDurationMs;

    const maxAds =
      this.config.podMaxAds && this.config.podMaxAds > 0
        ? this.config.podMaxAds
        : this.apiNumberAds && this.apiNumberAds > 0
          ? this.apiNumberAds
          : this.defaultPodMaxAds;

    const minDurationMs =
      this.config.podExactDuration && maxDurationMs != null ? maxDurationMs : 0;

    return { maxAds, minDurationMs, maxDurationMs };
  }

  generatePodVastUrl(
    baseUrl: string,
    podParams: { maxAds: number; minDurationMs: number; maxDurationMs: number },
    adPlayer: AdController,
    palNonce: PalNonceManager,
    inAdBreak: boolean
  ): string {
    const adWillPlayMuted = inAdBreak
      ? adPlayer.getOriginalMutedState()
      : this.video.muted;
    const envSignals = resolveVastEnvironmentSignals(!!this.config.ctvAdRequest);

    const urlWithMacros = applyVastMacros(baseUrl, {
      correlator: this.viewCorrelator,
      streamCorrelator: this.streamCorrelator,
      pod: this.podCounter > 0 ? this.podCounter : undefined,
      podMaxAds: podParams.maxAds,
      podMinDurationMs: podParams.minDurationMs,
      podMaxDurationMs: podParams.maxDurationMs,
      isCtv: envSignals.isCtv,
      contentUrl: envSignals.contentUrl,
      pageUrl:
        !envSignals.isCtv && typeof window !== "undefined"
          ? window.location.href
          : undefined,
      adWillPlayMuted,
      adWillAutoPlay: !!this.config.autoplay,
      appId: envSignals.appId,
      appName: envSignals.appName,
      sessionId: envSignals.sessionId,
      deviceId: envSignals.deviceId,
      deviceIdType: envSignals.deviceIdType,
      limitAdTracking: envSignals.limitAdTracking,
      deviceTypeHint: envSignals.deviceTypeHint,
      adTest: this.config.adTest,
      consent: this.consentSignals,
    });

    const finalUrl = palNonce.injectNonce(urlWithMacros);
    console.log(
      `[StormcloudVideoPlayer] Resolved optimized-pod request URL (pod=${this.podCounter}, pmad=${podParams.maxAds}, pmnd=${podParams.minDurationMs}, pmxd=${podParams.maxDurationMs ?? "?"}):`,
      finalUrl
    );
    return finalUrl;
  }

  // --- Tracking ---

  initializeTracking(): void {
    sendInitialTracking(this.config.licenseKey, buildAnalyticsContext(this.config))
      .then(() => {
        this.heartbeatInterval = window.setInterval(() => {
          this.sendHeartbeatIfNeeded();
        }, 5000);
      })
      .catch((error) => {
        if (this.debug) {
          console.warn(
            "[StormcloudVideoPlayer] Failed to send initial tracking:",
            error
          );
        }
        this.heartbeatInterval = window.setInterval(() => {
          this.sendHeartbeatIfNeeded();
        }, 5000);
      });
  }

  private sendHeartbeatIfNeeded(): void {
    const now = Date.now();
    if (!this.lastHeartbeatTime || now - this.lastHeartbeatTime > 30000) {
      this.lastHeartbeatTime = now;
      sendHeartbeat(this.config.licenseKey, buildAnalyticsContext(this.config)).catch((error) => {
        if (this.debug) {
          console.warn(
            "[StormcloudVideoPlayer] Failed to send heartbeat:",
            error
          );
        }
      });
    }
  }

  destroyTracking(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = undefined;
    }
  }
}
